import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";

export default function Users() {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    recipes: 0,
    cookingTotal: 0,
    avgRating: 0,
    productsTotal: 0,
  });

  useEffect(() => {
    fetchProfiles();
    fetchStats();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setProfiles(data);
    setLoading(false);
  };

  const fetchStats = async () => {
    const [profilesRes, cookingRes, recipesRes, productsRes, ratingRes] =
      await Promise.all([
        supabase.from("profiles").select("id", { count: "exact" }),
        supabase
          .from("cooking_history")
          .select("user_id, rating")
          .eq("status", "done"),
        supabase
          .from("recipes")
          .select("id", { count: "exact" })
          .eq("status", "published"),
        supabase.from("user_products").select("id", { count: "exact" }),
        supabase
          .from("cooking_history")
          .select("rating")
          .eq("status", "done")
          .not("rating", "is", null),
      ]);
    const activeUsers = new Set(cookingRes.data?.map((c: any) => c.user_id))
      .size;
    const ratings = ratingRes.data?.map((r: any) => r.rating) || [];
    const avgRating =
      ratings.length > 0
        ? (
            ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length
          ).toFixed(1)
        : 0;
    setStats({
      total: profilesRes.count || 0,
      active: activeUsers,
      recipes: recipesRes.count || 0,
      cookingTotal: cookingRes.data?.length || 0,
      avgRating: Number(avgRating),
      productsTotal: productsRes.count || 0,
    });
  };

  const formatDate = (d: string) =>
    d ? new Date(d).toLocaleDateString("ru-RU") : "—";

  const filtered = profiles.filter((p) =>
    p.id?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <h1 style={s.title}>👥 Пользователи</h1>
      <div style={s.statsGrid}>
        {[
          { num: stats.total, label: "Всего пользователей", color: "#2ECC71" },
          { num: stats.active, label: "Готовили хоть раз", color: "#3498db" },
          {
            num: stats.cookingTotal,
            label: "Всего приготовлений",
            color: "#9b59b6",
          },
          { num: stats.recipes, label: "Рецептов в базе", color: "#f39c12" },
          {
            num: stats.productsTotal,
            label: "Продуктов в холодильниках",
            color: "#e74c3c",
          },
          {
            num: stats.avgRating || "—",
            label: "Средняя оценка блюд",
            color: "#f1c40f",
          },
        ].map((stat, i) => (
          <div key={i} style={s.statCard}>
            <div style={{ ...s.statNum, color: stat.color }}>{stat.num}</div>
            <div style={s.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      <input
        style={{ ...s.search, marginBottom: 16 }}
        placeholder="Поиск по User ID..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <p>Загрузка...</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>User ID</th>
              <th style={s.th}>Тариф</th>
              <th style={s.th}>Истекает</th>
              <th style={s.th}>AI запросы</th>
              <th style={s.th}>Зарегистрирован</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} style={s.tr}>
                <td style={s.td}>
                  <span style={s.userId}>{p.id}</span>
                </td>
                <td style={s.td}>
                  <span
                    style={{
                      ...s.badge,
                      backgroundColor:
                        p.subscription_tier === "pro"
                          ? "#f3e5f5"
                          : p.subscription_tier === "basic"
                            ? "#e3f2fd"
                            : "#f5f5f5",
                      color:
                        p.subscription_tier === "pro"
                          ? "#9b59b6"
                          : p.subscription_tier === "basic"
                            ? "#3498db"
                            : "#888",
                    }}
                  >
                    {p.subscription_tier?.toUpperCase() || "FREE"}
                  </span>
                </td>
                <td style={s.td}>
                  {p.subscription_expires_at ? (
                    <span
                      style={{
                        color:
                          new Date(p.subscription_expires_at) < new Date()
                            ? "#e74c3c"
                            : "#222",
                      }}
                    >
                      {formatDate(p.subscription_expires_at)}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={s.td}>{p.ai_requests_count || 0}</td>
                <td style={s.td}>{formatDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, color: "#222", marginBottom: 24 },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    textAlign: "center",
  },
  statNum: { fontSize: 36, fontWeight: 700 },
  statLabel: { fontSize: 13, color: "#888", marginTop: 4 },
  search: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #eee",
    fontSize: 14,
    width: 300,
    outline: "none",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  thead: { backgroundColor: "#f9f9f9" },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: 13,
    color: "#888",
    fontWeight: 600,
  },
  tr: { borderBottom: "1px solid #f0f0f0" },
  td: { padding: "12px 16px", fontSize: 14, color: "#222" },
  userId: { fontSize: 12, fontFamily: "monospace", color: "#888" },
  badge: {
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },
};
